// The orchestrator chat is platform-admin only. Its run is conversationless
// and ephemeral, so the write surfaces a v3 run offers — questions, tool
// approvals, attachments — are all rejected by the API for it.
import { createElement, type ReactNode } from 'react';

import { type HarnessScope } from '@apps/web/features/phoebe_chat_v3/model/event_types';
import {
    type AgentProfile,
    type AgentProfilePorts,
    type AgentProfileResolver,
} from '@apps/web/features/phoebe_chat_v3/profiles/agent_profile';
import { type V3PhoebeRendererOverlay } from '@apps/web/features/phoebe_chat_v3/profiles/v3_phoebe_profile';
import { ChatHomeStage } from '@apps/web/features/phoebe_chat_v3/ui/chat_home';

export const ORCHESTRATOR_PROFILE_ID = 'v3-orchestrator';
export const ORCHESTRATOR_RENDERER_VERSION = 'v1';
export const ORCHESTRATOR_COPY = {
    emptyTitle: 'What should the orchestrator take on?',
    emptyBody: 'Describe the work and it will delegate to sub-agents.',
    unavailableBody: 'This conversation is not available.',
} as const;
export const ORCHESTRATOR_PLACEHOLDER = 'Message the orchestrator';
const ORCHESTRATOR_RENDERER_OVERLAY = {} satisfies V3PhoebeRendererOverlay;

function renderEmptyState(_scope: HarnessScope): ReactNode {
    return createElement(ChatHomeStage, {
        greeting: ORCHESTRATOR_COPY.emptyTitle,
    });
}

export function orchestratorAgentProfile(
    ports: AgentProfilePorts,
): AgentProfile {
    return {
        id: ORCHESTRATOR_PROFILE_ID,
        rendererVersion: ORCHESTRATOR_RENDERER_VERSION,
        displayName: 'Orchestrator',
        rendererOverlay: ORCHESTRATOR_RENDERER_OVERLAY,
        copy: ORCHESTRATOR_COPY,
        transport: ports.transport,
        features: {
            composer: {
                kind: 'text',
                placeholder: ORCHESTRATOR_PLACEHOLDER,
                send: ports.writes.sendMessage,
                uploads: false,
            },
            questions: { kind: 'none' },
            toolApprovals: { kind: 'none' },
            interrupt: { kind: 'supported', interrupt: ports.writes.interrupt },
        },
        emptyState: renderEmptyState,
    };
}

export function orchestratorAgentProfileResolver(
    profile: AgentProfile,
): AgentProfileResolver {
    return {
        id: profile.id,
        resolve(summary) {
            if (summary.run_type === 'orchestrator') {
                return { kind: 'matched', summary, profile };
            }
            return { kind: 'different_experience', summary };
        },
    };
}
